import React from 'react';
import { Box, Divider, Stack, Typography } from '@mui/material';
import { ReportDefinition } from '../Settings/reportDsl';
import {
  renderFormattedTableCell,
  resolveTableColumnDisplayAs,
  type TableColumnDisplayAs,
} from './reportTableCellDisplay';
import { formatEventTimestampLocal } from './eventTimeDisplay';

interface ReportDetailCardProps {
  report: ReportDefinition;
  /** The single matched row (dim_<i> / metric_<i> keys), or undefined. */
  row?: Record<string, any>;
}

interface DetailField {
  label: string;
  key: string;
  sourceField?: string;
  displayAs?: TableColumnDisplayAs;
}

/**
 * Renders a single event as a vertical list of `label : value` rows.
 *
 * Field order follows the report's `dimensions` then `metrics`. Per-field
 * rendering (links, enabled/disabled flags, filter JSON, capped counts) is
 * driven by `visualizations[0].options.columnDisplay`, reusing the exact
 * same formatters as the paginated table so the two stay consistent.
 */
export const ReportDetailCard: React.FC<ReportDetailCardProps> = ({ report, row }) => {
  const vis = report.visualizations?.[0];
  const columnDisplay = (vis?.options?.columnDisplay ?? {}) as Record<string, TableColumnDisplayAs>;

  const fields: DetailField[] = [];
  (report.query.dimensions || []).forEach((d, i) => {
    fields.push({
      label: d.label || `dim_${i}`,
      key: `dim_${i}`,
      sourceField: d.field,
      displayAs: resolveTableColumnDisplayAs(d.field, columnDisplay),
    });
  });
  (report.query.metrics || []).forEach((m, i) => {
    fields.push({
      label: m.label || `metric_${i}`,
      key: `metric_${i}`,
      sourceField: m.field,
      displayAs: resolveTableColumnDisplayAs(m.field, columnDisplay),
    });
  });

  if (!row) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>
        No matching event was found.
      </Typography>
    );
  }

  const renderValue = (f: DetailField): React.ReactNode => {
    const raw = row[f.key];
    const formatted = renderFormattedTableCell(raw, f.displayAs);
    if (formatted !== null) return formatted;
    if (f.sourceField === 'event_time') return formatEventTimestampLocal(raw) || '—';
    return raw === null || raw === undefined || raw === '' ? '—' : String(raw);
  };

  return (
    <Stack divider={<Divider flexItem />} sx={{ width: '100%' }}>
      {fields.map((f) => (
        <Box
          key={f.key}
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: '200px 1fr' },
            gap: { xs: 0.25, sm: 2 },
            py: 1,
            alignItems: 'start',
          }}
        >
          <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.secondary' }}>
            {f.label}
          </Typography>
          <Box sx={{ fontSize: 13, wordBreak: 'break-word' }}>{renderValue(f)}</Box>
        </Box>
      ))}
    </Stack>
  );
};
